import Query from "../models/query";
import QueryService from "../services/queryService";
import Pluralizeable from "../mixins/pluralizeable";
import Vue from "vue";

export default {
  mixins: [Pluralizeable],
  props: {
    state: Object,
    hasCollection: {
      type: Boolean,
      default: false
    },
    showResults: {
      type: Boolean,
      default: true
    },
    options: {
      type: Object,
      default: () => { return {}; }
    },
  },
  data: function() {
    return {
      rootResourceName: 'resources',
      query: new Query({direction: 'desc'}),
      queryService: <QueryService>null,
      fetch: null,
      searchProgress: null,
      showInvite: false,
      collectionQuery: new Query({per_page: 20, direction: 'desc'}),
      collectionQueryService: <QueryService>null,
      fetchNextPage: null,
      collection: [],
      isLoading: false,
    };
  },
  beforeMount() {
    this.fetch = this.queryService.fetch.bind(this.queryService);

    if (this.hasCollection) {
      this.fetchNextPage = this.queryService.fetch.bind(this.collectionQueryService);
      Vue.set(this.collectionResults, 'resources', []);
      Vue.set(this.collectionResults, 'pagination', {});
    }

    Vue.set(this.results, 'resources', []);
    Vue.set(this.results, 'pagination', {});
  },
  mounted() {
    if (!this.hasCollection) return;
    const $list = (<any>$)(this.$refs.collection);
    $list.on('scroll', this.fetchOnScroll);
    this.fetchMoreCollectionResources();
  },
  computed: {
    q: {
      get() {
        return this.query.q;
      },
      set(value) {
        if (value.length > 0) {
          this.searchProgress = 'Searching...';
          this.query.q = value;
          this.showInvite = false;
          this.fetch(this.query).then(result => {
            this.query.results = result;
            this.showInvite = true;
          }).catch(err => {
            console.error(err)
          }).finally(() => {
            this.updateSearchProgress();
          });
        } else {
          this.searchProgress = null;
          this.query.clear();
          this.queryService.kill();
        }
      }
    },
    hasQ() {
      return this.q.length > 0;
    },
    results() {
      return this.query.results;
    },
    resourcesFromQuery() {
      return this.results[this.rootResourceName];
    },
    hasResourcesFromQuery() {
      return this.resourcesFromQuery && this.resourcesFromQuery.length > 0;
    },
    collectionResults() {
      if (!this.hasCollection) return;
      return this.collectionQuery.results;
    },
    resourcesFromCollectionQuery() {
      if (!this.hasCollection) return;
      return this.collectionResults[this.rootResourceName];
    },
    isCollectionLastPage() {
      if (!this.hasCollection) return;
      return this.collectionResults.pagination.is_last_page === true;
    },
    nextCollectionLoadCount() {
      if (!this.hasCollection) return;
      return this.collectionResults.pagination.next_page_count || 0;
    },
    nextCollectionLoadCountDisplay() {
      if (!this.hasCollection) return;
      if (this.isCollectionLastPage) return "You've reached the end of the list.";

      const count = this.nextCollectionLoadCount;
      const plural = this.$options.filters.pluralize('member', count, false);
      const countText = this.nextCollectionLoadCount ? `${count} more ${plural}` : plural;
      return `Loading ${countText}...`;
    },
    searchClasses() {
      return {
        'search': true,
        'list': true,
        'dropdown-menu': this.hasCollection,
        'show': this.hasCollection,
      };
    },
    collectionClasses() {
      return {
        'search': true,
        'list': true,
      };
    },
  },
  methods: {
    fetchDefaultQuery() {
      const tempQuery = new Query({direction: 'desc'});
      tempQuery.q = "*";
      this.fetch(tempQuery).then(result => {
        this.query.results = result;
      });
    },
    resetModal(event = null) {
      this.$emit('toggle-modal', event);
      this.query.clear();
      this.queryService.kill();
    },
    resetSearch(shouldReset: boolean = true): void {
      if (shouldReset) {
        this.query.clear();
      }
    },
    resetSearchOrModal() {
      if (this.hasQ) {
        this.resetSearch();
      } else {
        this.resetModal();
      }
    },
    resetCollectionAfterDecision() {
      this.collectionQuery.reset();
      this.collection = [];
      this.collectionResults.resources = [];
      this.collectionResults.pagination = {};
    },
    clickOutOfSearch(event) {
      const $target = <any>$(event.target);
      if ($target.closest('.search-wrapper-auto').length === 0) {
        // if not clicking inside the search itself
        this.resetSearch(this.hasCollection);
      }
    },
    fetchMoreCollectionResources() {
      if (!this.hasCollection) return;
      if (!this.isLoading && !this.isCollectionLastPage) {
        this.isLoading = true;
        this.fetchNextPage(this.collectionQuery).then(result => {
          this.collectionQuery.page++;
          this.collectionQuery.results = result;
          this.collection.push(...this.resourcesFromCollectionQuery);
        }).finally(() => this.isLoading = false);
      }
    },
    fetchOnScroll(event) {
      if (!this.hasCollection) return;
      const $list = (<any>$)(event.target);
      const scrollHeight = $list.prop('scrollHeight');
      const top = $list.scrollTop();
      const height = $list.height();
      const threshold = 200;
      if (scrollHeight - top - height <= threshold) {
        this.fetchMoreCollectionResources();
      }
    },
    updateResource(event) {
      if (event.shouldRefresh && this.hasCollection) {
        this.resetSearch();
        this.resetCollectionAfterDecision();
        this.fetchMoreCollectionResources();
      } else {
        const array = (() => {
          if (this.hasCollection) {
            return this.collection;
          } else if (this.resourcesFromQuery) {
            return this.resourcesFromQuery;
          } else {
            return null;
          }
        })();
        if (array) {
          const resource = event.resource;
          const collectionResourceIndex = array.findIndex(u => u[event.listItemIdKey] === resource[event.idKey]);
          if (collectionResourceIndex > -1) {
            Vue.set(array, collectionResourceIndex, resource);
          }
        }
      }
    },
    updateSearchProgress() {
      if (!this.showResults) {
          if (this.results.member_exists) {
            this.searchProgress = 'Member already exists';
          } else if (!this.results.can_invite_q) {
            this.searchProgress = 'Invalid email address';
          } else if (this.results.pending_users && this.results.pending_users.filter(x => { return x.email === this.q}).length > 0) {
            this.searchProgress = null;
          } else {
            this.searchProgress = null;
          }
      } else {
        if (!this.hasResourcesFromQuery) {
          if (this.results.member_exists) {
            this.searchProgress = 'Member already exists';
          } else if (!this.results.can_invite_q) {
            this.searchProgress = 'Invalid email address';
          } else if (this.results.can_invite_q) {
            this.searchProgress = null;
          } else if (this.results.pending_users && this.results.pending_users.filter(x => { return x.email === this.q}).length > 0 ) {
            this.searchProgress = null;
          } else {
            this.searchProgress = 'No matching results...';
          }
        } else {
          this.searchProgress = null;
        }
      }
    }
  }
};
